import { Col, Form, Row, Tabs, message, theme, Button, Card, Space, Typography } from 'antd'
import React, { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronRight,
  faWarning,
  faSave,
  faTimes,
  faPlus,
  faUser,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA } from '../../../../lib/utils'
import CustomRow from '../../../../reusable-components/CustomRow'
import CustomButton from '../../../../reusable-components/CustomButton'
import useAddPatient from '../../hooks/useAddPatient'
import PatientDependants from '../PatientDependants'
import PatientDetailsForm from './PatientDetailsForm'
import MedicalAidForm from './MedicalAidForm'
import ContactDetailsForm from './ContactDetailsForm'
import AddressDetailsForm from './AddressDetailsForm'

const { Title, Text } = Typography

const AddPatientForm = ({
  refreshView,
  type,
  isBooking,
  setBookingPatient,
  setAddingForBooking,
  isDependant,
  mainPatientInfo,
  resetFunction,
  editRecord
}) => {
  const [savedPatientId, setSavedPatientId] = useState(null)
  const [activeTabKey, setActiveTabKey] = useState('1')
  const [formChangeCounter, setFormChangeCounter] = useState(0)

  const {
    loading,
    patientForm,
    specialFormState,
    setSpecialFormState,
    dependants,
    idType,
    schemeLoading,
    schemeData,
    plansLoading,
    plansData,
    planOptionsLoading,
    planOptionData,
    handleSubmit,
    handleReset,
    handleDepChange,
    handleAddDep,
    handleRemoveDep,
    setIdType,
    debouncedSchemeFilter,
    handleGetMedicalAidPlans,
    handleGetMedicalAidPlanOptions,
    closeTab,
    stayOnPage,
    setStayOnPage,
    savedPatient
  } = useAddPatient({
    refreshView,
    type,
    isBooking,
    setBookingPatient,
    setAddingForBooking,
    isDependant,
    mainPatientInfo,
    resetFunction,
    shouldStayOnPage: true,
    onPatientSaved: (patientData) => {
      setSavedPatientId(patientData?._id || null)
      if (!isDependant) {
        setActiveTabKey('2')
      }
    },
    editRecord
  })

  // Watch form values to trigger button state updates
  const watchedValues = Form.useWatch([], patientForm)

  useEffect(() => {
    if (savedPatient?._id) {
      setSavedPatientId(savedPatient._id)

      // After saving a dependant, call refreshView to update the dependants list
      if (isDependant && refreshView) {
        refreshView()
      }
    }
  }, [savedPatient, isDependant, refreshView])

  useEffect(() => {
    setFormChangeCounter((prev) => prev + 1)
  }, [watchedValues])

  const { token } = theme.useToken()
  const [dependantSameAddress, setDependantSameAddress] = useState(isDependant)
  const [dependantSameContact, setDependantSameContact] = useState(isDependant)

  // Apply main member's contact details when dependantSameContact is true
  useEffect(() => {
    if (isDependant && dependantSameContact && mainPatientInfo) {
      // Set the dependant's contact details to match the main member's
      patientForm.setFieldsValue({
        email: mainPatientInfo.email,
        phoneNumber: mainPatientInfo.phoneNumber?.replace('+27', '') || ''
      })
    } else if (isDependant && !dependantSameContact) {
      // Clear the contact fields when the switch is turned off
      patientForm.setFieldsValue({
        email: '',
        phoneNumber: ''
      })
    }
  }, [dependantSameContact, isDependant, mainPatientInfo, patientForm])

  const handleTabChange = (key) => {
    setActiveTabKey(key)
  }

  // Check if all required fields are filled for the current tab
  const isFormValid = useMemo(() => {
    try {
      const formValues = patientForm.getFieldsValue()

      // Basic required fields for patient
      const hasFirstName = formValues.firstName?.trim()
      const hasLastName = formValues.lastName?.trim()
      const hasIdNo = formValues.idNo?.trim()
      const hasDateOfBirth = formValues.dateOfBirth
      const hasGender = formValues.gender

      return hasFirstName && hasLastName && hasIdNo && hasDateOfBirth && hasGender
    } catch (error) {
      return false
    }
  }, [patientForm, formChangeCounter, watchedValues])

  // Get the appropriate title based on user type
  const getTitleInfo = () => {
    if (isDependant) {
      return {
        title: 'Add Dependant',
        subtitle: 'Create a new dependant profile linked to the main member'
      }
    }
    return {
      title: editRecord ? 'Edit Patient' : 'Add Patient',
      subtitle: editRecord
        ? 'Update patient information and details'
        : 'Create a new patient profile with medical aid information'
    }
  }

  const titleInfo = getTitleInfo()

  return (
    <>
      {/* Enhanced Header */}
      <Card
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}
        bodyStyle={{ padding: '20px 32px' }}
      >
        <Row justify='space-between' align='middle'>
          <Col>
            <Space size={16}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faUser} style={{ color: token.colorPrimary, fontSize: '20px' }} />
              </div>
              <div>
                <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                  {titleInfo.title}
                </Title>
                <Text type='secondary' style={{ fontSize: '16px' }}>
                  {titleInfo.subtitle}
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space size={12}>
              <Button
                onClick={() => {
                  if (isDependant) {
                    resetFunction()
                  } else {
                    resetFunction()
                    closeTab()
                  }
                }}
                style={{
                  borderRadius: '6px',
                  height: '40px',
                  fontWeight: '500'
                }}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faXmark} style={{ marginRight: '8px' }} />
                Cancel
              </Button>
              <Button
                type='primary'
                loading={loading}
                disabled={!isFormValid}
                onClick={() => patientForm.submit()}
                style={{
                  background: !isFormValid ? undefined : token.actionColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  height: '40px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderRadius: '6px'
                }}
                size='large'
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                {activeTabKey === '1' ? 'Confirm Account Details' : 'Save Changes'}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <CustomRow justify='center' className='basic-card'>
        <Col span={24}>
          <Tabs
            activeKey={activeTabKey}
            onChange={handleTabChange}
            size='large'
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
            tabBarStyle={{
              padding: '0 32px',
              margin: 0,
              background: hexToRGBA(token.colorBgContainer, 0.5),
              borderRadius: '16px 16px 0 0'
            }}
            items={[
              {
                key: '1',
                label: (
                  <span style={{ fontWeight: '500', fontSize: '16px' }}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: token.colorPrimary }} />
                    {isDependant ? 'Dependant Profile' : 'Patient Details'}
                  </span>
                ),
                children: (
                  <div style={{ padding: '32px' }}>
                    <Form
                      initialValues={{
                        firstName: '',
                        lastName: '',
                        idNo: '',
                        email: '',
                        phoneNumber: '',
                        residentialAddress: '',
                        medicalAid: mainPatientInfo
                          ? {
                              ...mainPatientInfo?.medicalAid,
                              dependantNumber: null
                            }
                          : {}
                      }}
                      layout='vertical'
                      form={patientForm}
                      onFinish={handleSubmit}
                      onFinishFailed={() => {
                        message.error('Patient information missing.')
                      }}
                      onFieldsChange={() => {
                        setFormChangeCounter((prev) => prev + 1)
                      }}
                    >
                      <Row gutter={[32, 24]}>
                        <Col xs={24} lg={12}>
                          <Space direction='vertical' size={24} style={{ width: '100%' }}>
                            <PatientDetailsForm
                              form={patientForm}
                              specialFormState={specialFormState}
                              setSpecialFormState={setSpecialFormState}
                              idType={idType}
                              setIdType={setIdType}
                              token={token}
                            />

                            <ContactDetailsForm
                              form={patientForm}
                              specialFormState={specialFormState}
                              setSpecialFormState={setSpecialFormState}
                              token={token}
                              isDependant={isDependant}
                              dependantSameContact={dependantSameContact}
                              setDependantSameContact={setDependantSameContact}
                              mainPatientInfo={mainPatientInfo}
                            />

                            <AddressDetailsForm
                              form={patientForm}
                              token={token}
                              isDependant={isDependant}
                              dependantSameAddress={dependantSameAddress}
                              setDependantSameAddress={setDependantSameAddress}
                            />
                          </Space>
                        </Col>

                        <Col xs={24} lg={12}>
                          <MedicalAidForm
                            form={patientForm}
                            schemeLoading={schemeLoading}
                            schemeData={schemeData}
                            plansLoading={plansLoading}
                            plansData={plansData}
                            planOptionsLoading={planOptionsLoading}
                            planOptionData={planOptionData}
                            debouncedSchemeFilter={debouncedSchemeFilter}
                            handleGetMedicalAidPlans={handleGetMedicalAidPlans}
                            handleGetMedicalAidPlanOptions={handleGetMedicalAidPlanOptions}
                            token={token}
                          />
                        </Col>
                      </Row>
                    </Form>
                  </div>
                )
              },
              !isDependant && {
                key: '2',
                label: (
                  <span style={{ fontWeight: '500', fontSize: '16px' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px', color: token.colorSecondary }} />
                    Dependants
                  </span>
                ),
                children: (
                  <div style={{ padding: '32px' }}>
                    <Row justify='center'>
                      <Col span={24}>
                        <Card
                          style={{
                            marginBottom: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${hexToRGBA(token.colorWarning, 0.2)}`,
                            background: hexToRGBA(token.colorWarning, 0.02)
                          }}
                          bodyStyle={{ padding: '20px', textAlign: 'center' }}
                        >
                          <Space size={8}>
                            <FontAwesomeIcon style={{ color: token.colorWarning, fontSize: '16px' }} icon={faWarning} />
                            <Text strong style={{ color: token.colorWarning }}>
                              Please make sure to add all dependants for this patient
                            </Text>
                            <FontAwesomeIcon style={{ color: token.colorWarning, fontSize: '16px' }} icon={faWarning} />
                          </Space>
                        </Card>

                        <PatientDependants
                          userRef={savedPatientId}
                          mainPatientInfo={
                            savedPatient || {
                              ...patientForm.getFieldsValue(),
                              _id: savedPatientId
                            }
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                )
              }
            ].filter(Boolean)}
          />
        </Col>
      </CustomRow>
    </>
  )
}

export default AddPatientForm
